import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { IProduct, ISubprocessFieldValidation, IPrerequisiteValidation } from './types';
import { productService } from './productService';
import { productMapper } from './productMapper';

interface ProductState {
  products: IProduct[];
  selectedProduct: IProduct | null;
  subprocessFieldValidations: Record<string, ISubprocessFieldValidation>;
  prerequisiteValidations: Record<string, IPrerequisiteValidation>;
  loading: boolean;
  error: string | null;
}

const initialState: ProductState = {
  products: [],
  selectedProduct: null,
  subprocessFieldValidations: {},
  prerequisiteValidations: {},
  loading: false,
  error: null
};

// Async thunks
export const fetchProducts = createAsyncThunk(
  'product/fetchProducts',
  async () => {
    const response = await productService.getAll();
    return response.data;
  }
);

export const fetchProductById = createAsyncThunk(
  'product/fetchProductById',
  async (id: string) => {
    const response = await productService.getById(id);
    return response.data;
  }
);

export const createProduct = createAsyncThunk(
  'product/createProduct',
  async (product: Partial<IProduct>) => {
    const response = await productService.create(productMapper.toDTO(product as IProduct));
    return response.data;
  }
);

export const updateProduct = createAsyncThunk(
  'product/updateProduct',
  async ({ id, product }: { id: string; product: Partial<IProduct> }) => {
    const response = await productService.update(id, productMapper.toDTO(product as IProduct));
    return response.data;
  }
);

export const deleteProduct = createAsyncThunk(
  'product/deleteProduct',
  async (id: string) => {
    await productService.delete(id);
    return id;
  }
);

export const validateSubprocessField = createAsyncThunk(
  'product/validateSubprocessField',
  async ({ productId, fieldId }: { productId: string; fieldId: string }) => {
    const response = await productService.validateSubprocessField(productId, fieldId);
    return { fieldId, validation: response.data };
  }
);

export const validatePrerequisites = createAsyncThunk(
  'product/validatePrerequisites',
  async ({ productId, activityId }: { productId: string; activityId: string }) => {
    const response = await productService.validatePrerequisites(productId, activityId);
    return { activityId, validation: response.data };
  }
);

const productSlice = createSlice({
  name: 'product',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearValidations: (state) => {
      state.subprocessFieldValidations = {};
      state.prerequisiteValidations = {};
    },
    clearSelectedProduct: (state) => {
      state.selectedProduct = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch products
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch products';
      })
      // Fetch product by id
      .addCase(fetchProductById.fulfilled, (state, action: PayloadAction<IProduct>) => {
        state.selectedProduct = action.payload;
        state.loading = false;
      })
      .addCase(fetchProductById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchProductById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch product';
      })
      // Create product
      .addCase(createProduct.fulfilled, (state, action) => {
        state.products.push(action.payload);
      })
      // Update product
      .addCase(updateProduct.fulfilled, (state, action) => {
        const index = state.products.findIndex(p => p._id === action.payload._id);
        if (index !== -1) {
          state.products[index] = action.payload;
        }
        if (state.selectedProduct?._id === action.payload._id) {
          state.selectedProduct = action.payload;
        }
      })
      // Delete product
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.products = state.products.filter(p => p._id !== action.payload);
        if (state.selectedProduct?._id === action.payload) {
          state.selectedProduct = null;
        }
      })
      // Validate subprocess field
      .addCase(validateSubprocessField.fulfilled, (state, action) => {
        state.subprocessFieldValidations[action.payload.fieldId] = action.payload.validation;
      })
      // Validate prerequisites
      .addCase(validatePrerequisites.fulfilled, (state, action) => {
        state.prerequisiteValidations[action.payload.activityId] = action.payload.validation;
      });
  }
});

export const { clearError, clearValidations, clearSelectedProduct } = productSlice.actions;
export default productSlice.reducer; 